import {
  ProductInfo,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
  WALK_IN_DAILY_RATE_PRODUCT_KEY,
  WALK_IN_HOURLY_RATE_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';

/**
 * The three ways a traveler can pay for storage at a shop (CTR-51, time-of-booking pricing):
 * booked online before the drop-off day, booked online on the drop-off day, or walked in
 * without a booking.
 */
export type StoragePriceTierId = 'advance' | 'sameDay' | 'walkIn';

export type StoragePriceUnit = 'day' | 'hour';

export interface StoragePriceTier {
  id: StoragePriceTierId;
  /** Per bag per day. */
  daily: number;
  /** Per bag per hour. */
  hourly: number;
}

export interface StorageRates {
  daily?: number | null;
  hourly?: number | null;
}

/**
 * The tiers to show, cheapest first. Advance is always shown. Same-day is shown only when it differs
 * from advance in either unit: a Same day row repeating the Day ahead prices says nothing, and
 * while same-day is priced at parity that would be every shop. A shop with no same-day price is
 * charged the advance rates on the day, so it has no Same day row either.
 * Walk-in is shown only when the shop has both walk-in rates: a shop without them takes no
 * walk-ins, and an online-only shop must not advertise a price nobody can get.
 */
export function buildStoragePriceTiers(rates: {
  advance: { daily: number; hourly: number };
  sameDay?: StorageRates | null;
  walkIn?: StorageRates | null;
}): StoragePriceTier[] {
  const { advance, sameDay, walkIn } = rates;
  const sameDayDaily = priceOr(sameDay?.daily, advance.daily);
  const sameDayHourly = priceOr(sameDay?.hourly, advance.hourly);
  const tiers: StoragePriceTier[] = [{ id: 'advance', daily: advance.daily, hourly: advance.hourly }];
  if (sameDayDaily !== advance.daily || sameDayHourly !== advance.hourly) {
    tiers.push({ id: 'sameDay', daily: sameDayDaily, hourly: sameDayHourly });
  }
  const walkInDaily = walkIn?.daily;
  const walkInHourly = walkIn?.hourly;
  if (isPrice(walkInDaily) && isPrice(walkInHourly)) {
    tiers.push({ id: 'walkIn', daily: walkInDaily, hourly: walkInHourly });
  }
  return tiers;
}

/** The tiers from a shop's product list, or none when it has no advance storage rates. */
export function storagePriceTiersFromProducts(products: ProductInfo[] | null | undefined): StoragePriceTier[] {
  const cost = (key: string) => products?.find((product) => product.key === key)?.cost;
  const daily = cost(STORAGE_DAY_PRODUCT_KEY);
  const hourly = cost(STORAGE_HOUR_PRODUCT_KEY);
  if (!isPrice(daily) || !isPrice(hourly)) {
    return [];
  }
  return buildStoragePriceTiers({
    advance: { daily, hourly },
    sameDay: { daily: cost(SAME_DAY_STORAGE_DAY_PRODUCT_KEY), hourly: cost(SAME_DAY_STORAGE_HOUR_PRODUCT_KEY) },
    walkIn: { daily: cost(WALK_IN_DAILY_RATE_PRODUCT_KEY), hourly: cost(WALK_IN_HOURLY_RATE_PRODUCT_KEY) },
  });
}

/**
 * Whether a price-details view should show the table rather than a plain hourly / daily price: only
 * when booking today is priced differently from booking ahead, so there is a second row to show.
 */
export function shouldShowStoragePriceTable(tiers: StoragePriceTier[]): boolean {
  return tiers.some((tier) => tier.id === 'sameDay');
}

/**
 * Whether to keep the "walk-in rates may be higher" note. The table has no walk-in row (walking in is
 * a same-day booking), so the note stays whenever the shop has walk-in prices, and wherever the plain
 * price is shown instead of the table.
 */
export function shouldShowWalkInNote(tiers: StoragePriceTier[]): boolean {
  return !shouldShowStoragePriceTable(tiers) || tiers.some((tier) => tier.id === 'walkIn');
}

function isPrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function priceOr(value: number | null | undefined, fallback: number): number {
  return isPrice(value) ? value : fallback;
}
